/*
 * PDP variant guard — two layers against "lit swatch != variant in the cart form".
 *
 * The swatches are server-rendered and paint at FCP, but <variant-selects> (assets/global.js)
 * only attaches its `change` listener once the deferred scripts run, just before
 * DOMContentLoaded — measured at DCL -0.4 ms to +5 ms, i.e. ~0.7 s after FCP on a cold 4G
 * load. A tap inside that window checks the radio and nothing else: the composite, the price
 * and input[name="id"] all keep the server-rendered variant, and nothing ever fires `change`
 * again, so the wrong variant is what reaches the cart.
 *
 * Layer 1 — reconcile once at DOMContentLoaded. NOT window.load: `load` waits for every image
 * to finish, which is seconds on 4G, and a tap in that gap would stay broken the whole time.
 * The reconcile is wrapped in setTimeout 0 so it runs after the other DOMContentLoaded
 * listeners in the page. It compares the lit radios with the variant the server rendered
 * ([data-selected-variant]) and, on a skew, dispatches ONE bubbling `change` event on the radio
 * that disagrees — Dawn's own listener, pdp-mockup.js and pdp-sticky-atc.js all take it from
 * there. A consistent page dispatches nothing and costs no section-rendering request.
 *
 * Layer 2 — a backstop on the add-to-cart form's `submit`. It has to be a CAPTURE listener on
 * document: product-form.js binds its own submit listener in its constructor, long before this
 * file runs, so a bubble listener here would fire after the /cart/add fetch is already away.
 * On a skew it holds the submit back (preventDefault + stopPropagation, so product-form.js
 * never even enters its loading state), reconciles, waits for input[name="id"] to catch up and
 * re-submits ONCE. The sticky bar is covered for free: pdp-sticky-atc.js clicks the main
 * button, so it goes through the same form submit.
 *
 * NOT covered: the dynamic checkout button ({{ form | payment_button }}). It sits inside this
 * same form and reads the same input[name="id"], but Shopify drives it from its own click
 * handler and never fires the form's submit event, so layer 2 cannot see it. Layer 1 is what
 * protects it, and that is enough: measured cold on Fast 4G, product A, 5 runs, <shop-pay-wallet-
 * button> only becomes clickable at DCL +76/+207/+104/+231/+371 ms - i.e. 59 to 486 ms AFTER
 * layer 1 has finished reconciling (DCL +10 to +17 ms) - and it renders below the fold, so the
 * shopper has to scroll down to it as well; there is no window in which it can be tapped on a
 * skewed variant, and therefore nothing for a layer 2 to catch.
 */
(() => {
  const FORM_PREFIX = 'product-form-';
  const SELECTS_PREFIX = 'variant-selects-';
  const RECONCILE_TIMEOUT = 6000;
  const POLL_INTERVAL = 50;

  /* ---------- reading the DOM ---------- */

  function serverVariant(selects) {
    try {
      return JSON.parse(selects.querySelector('script[data-selected-variant]').textContent);
    } catch {
      return null;
    }
  }

  // One fieldset per option, in product.options_with_values order — the same order
  // variant.options uses, and the same order Dawn's own getAllSelectedOptions() relies on.
  function litRadios(selects) {
    return Array.from(selects.querySelectorAll('fieldset')).map((fieldset) =>
      fieldset.querySelector('input[type="radio"]:checked')
    );
  }

  // The first lit radio whose value disagrees with the rendered variant, or null.
  function firstSkew(selects, variant) {
    if (!variant || !Array.isArray(variant.options)) return null;
    const radios = litRadios(selects);
    if (radios.length !== variant.options.length) return null; // a shape we do not understand
    for (let i = 0; i < radios.length; i++) {
      if (radios[i] && radios[i].value !== variant.options[i]) return radios[i];
    }
    return null;
  }

  // NOT form.id: HTMLFormElement's named getter makes `form.id` return the <input name="id">
  // in this very form, not the element's id string.
  function formId(form) {
    return form.getAttribute('id') || '';
  }

  function selectsForForm(form) {
    const id = formId(form);
    if (id.startsWith(FORM_PREFIX)) {
      const byId = document.getElementById(SELECTS_PREFIX + id.slice(FORM_PREFIX.length));
      if (byId) return byId;
    }
    // The radios live outside the <form> and point at it with form="<id>".
    const radio = id && document.querySelector(`variant-selects input[type="radio"][form="${CSS.escape(id)}"]`);
    return radio ? radio.closest('variant-selects') : null;
  }

  /* ---------- layer 1: reconcile at DOMContentLoaded ---------- */

  function reconcile(selects) {
    const skew = firstSkew(selects, serverVariant(selects));
    if (!skew) return false;
    skew.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  let reconciled = false;
  function reconcileOnce() {
    if (reconciled) return;
    reconciled = true;
    document.querySelectorAll('variant-selects').forEach(reconcile);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(reconcileOnce, 0), { once: true });
  } else {
    setTimeout(reconcileOnce, 0);
  }

  /* ---------- layer 2: backstop at submit ---------- */

  // product-info.js REPLACES the <variant-selects> element on every variant change, so every
  // check re-queries it instead of holding a reference.
  function isConsistent(form) {
    const selects = selectsForForm(form);
    const idInput = form.querySelector('input[name="id"]');
    if (!selects || !idInput) return true; // nothing to judge — never block the shopper
    const variant = serverVariant(selects);
    if (!variant) return true;
    if (String(variant.id) !== idInput.value) return false;
    return !firstSkew(selects, variant);
  }

  function waitForConsistency(form) {
    const deadline = Date.now() + RECONCILE_TIMEOUT;
    return new Promise((resolve) => {
      const tick = () => {
        if (isConsistent(form)) return resolve(true);
        if (Date.now() >= deadline) return resolve(false);
        setTimeout(tick, POLL_INTERVAL);
      };
      setTimeout(tick, POLL_INTERVAL);
    });
  }

  const held = new WeakSet(); // a reconcile is in flight for this form
  const resubmitting = new WeakSet(); // this submit is ours — let it straight through

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
      if (resubmitting.has(form)) return; // our own re-submit: exactly one, never a loop

      // A second tap while a reconcile is in flight would start a second reconcile and end in
      // two adds, so it is swallowed instead.
      if (held.has(form)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (isConsistent(form)) return;

      event.preventDefault();
      event.stopPropagation();
      held.add(form);

      const selects = selectsForForm(form);
      const target = firstSkew(selects, serverVariant(selects)) || litRadios(selects).find(Boolean);
      target?.dispatchEvent(new Event('change', { bubbles: true }));

      waitForConsistency(form).then((ok) => {
        held.delete(form);
        if (!ok) return; // could not reconcile: add nothing, the next tap tries again
        const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
        resubmitting.add(form);
        try {
          form.requestSubmit(submitter);
        } catch {
          form.requestSubmit();
        } finally {
          resubmitting.delete(form);
        }
      });
    },
    true
  );
})();
